/** A list of memory notes: readable title, size, last change. A row opens to
 * show the note's text as preformatted plain text (never HTML, never linked:
 * a model wrote it and it may hold text copied from documents). */
import { useState } from 'react';
import { Button } from '../../../shared/ui/button';
import type { MemoryItem } from '../../../services/BotMemoryService';
import { formatBytes, formatChanged, pathParts } from './botMemoryFormat';

interface BotMemoryNotesListProps {
  notes: MemoryItem[];
  /** Resolves the note's text when a row is opened. */
  load: (note: MemoryItem) => Promise<string>;
  onDelete?: (note: MemoryItem) => void;
  onEdit?: (note: MemoryItem) => void;
  emptyText: string;
  busyId?: string | null;
}

export function BotMemoryNotesList({ notes, load, onDelete, onEdit, emptyText, busyId }: BotMemoryNotesListProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [failedId, setFailedId] = useState<string | null>(null);

  if (!notes.length) return <p className="text-sm text-muted-foreground">{emptyText}</p>;

  const toggle = async (note: MemoryItem) => {
    if (openId === note.id) { setOpenId(null); return; }
    setOpenId(note.id);
    setFailedId(null);
    const cached = texts[`${note.id}:${note.sha256}`];
    if (cached !== undefined) return;
    try {
      const text = await load(note);
      setTexts((previous) => ({ ...previous, [`${note.id}:${note.sha256}`]: text }));
    } catch { setFailedId(note.id); }
  };

  return (
    <ul className="divide-y divide-separator/60 rounded-md border border-separator">
      {notes.map((note) => {
        const { folder, title } = pathParts(note.path);
        const open = openId === note.id;
        const text = texts[`${note.id}:${note.sha256}`];
        return (
          <li key={note.id} className="px-3 py-2">
            <div className="flex items-start gap-2">
              <button type="button" aria-expanded={open} onClick={() => void toggle(note)} className="min-w-0 flex-1 text-left">
                <span className="block break-words text-sm font-medium">
                  {folder && <span className="font-normal text-muted-foreground">{folder}</span>}{title}
                </span>
                <span className="block text-xs text-muted-foreground">{formatBytes(note.sizeBytes)}, changed {formatChanged(note.updatedAt)}</span>
              </button>
              {onEdit && <Button size="sm" variant="ghost" disabled={busyId === note.id} onClick={() => onEdit(note)}>Edit</Button>}
              {onDelete && <Button size="sm" variant="ghost" className="text-destructive" disabled={busyId === note.id} onClick={() => onDelete(note)}>Delete</Button>}
            </div>
            {open && (
              failedId === note.id ? <p role="alert" className="mt-2 text-xs text-destructive">This note could not be loaded.</p>
              : text === undefined ? <p role="status" className="mt-2 text-xs text-muted-foreground">Loading</p>
              : <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded bg-muted/50 p-2 text-xs">{text || 'This note is empty.'}</pre>
            )}
          </li>
        );
      })}
    </ul>
  );
}
